/**
 * Harmony Generator
 * Per R-4: Focused class for color harmony generation
 * Handles triadic, analogous, complementary, and other harmony schemes
 */
import { ColorManipulator } from '../color/ColorManipulator.js';
import { ColorConverter, normalizeDeltaEFormula, } from '../color/ColorConverter.js';
/**
 * The ΔE formula these per-type harmony methods rank by when a caller does not
 * say. Was hard-coded `'cie76'`, which meant harmony ranked by a metric that
 * disagrees with `ciede2000` — the suite default every other tool uses — on
 * about 31% of dye pairs.
 *
 * ⚠️ Changing this is CORRECT but it is NOT the fix the 2026-09-03 fact-check
 * described, because the live harmony path moved out from under it. Since the
 * harmony convergence (PR #159) every surface — web app, Discord bot,
 * og-worker — goes through `HarmonySelector.generateHarmonySlots`, which takes
 * `matchingMethod` as a REQUIRED config field and is given `ciede2000`
 * explicitly by all three. The `find*Dyes()` methods below are reached only
 * through the `DyeService` façade, which nothing in this monorepo calls; they
 * survive as published npm API. So this default was already unreachable in
 * production, and aligning it changes no shipped behaviour — it stops the
 * published API contradicting the documented suite default.
 */
const DEFAULT_HARMONY_DELTA_E = 'ciede2000';
/**
 * Default ΔE tolerance per formula.
 *
 * BUG-059: these used to be a two-way ternary — `ciede2000 ? 25 : 40` — which
 * silently gave `'oklab'` CIE76's tolerance. OKLAB ΔE runs roughly 0-1, so 40
 * admitted the entire dye set. Each value sits just outside its formula's
 * widest calibrated `harmony` band cut in `config/band-vocabulary.ts`
 * (ciede2000 20, cie76 26.2, oklab 0.174), which is the relationship the
 * original two already had.
 *
 * The oklab value moved 0.13 -> 0.21 in 5.1.0, when `getDeltaE_Oklab` became
 * ΔEOK2 and its widest harmony cut moved 0.107 -> 0.174. The ratio to that cut
 * is preserved (~1.21), so the tolerance still means the same thing relative
 * to the bands; leaving 0.13 behind would have quietly tightened oklab harmony
 * matching to roughly half its intended reach.
 */
const DEFAULT_DELTA_E_TOLERANCE = {
    ciede2000: 25,
    cie76: 40,
    oklab: 0.21,
};
/**
 * ΔE between two colours under a canonical formula.
 *
 * BUG-059: both call sites used a two-way ternary (`ciede2000 ? 2000 : 76`),
 * which quietly routed `'oklab'` into the CIE76 branch — an oklab caller got
 * CIE76 numbers under an oklab label, then compared them against an oklab
 * tolerance. Lab values are passed in because they are pre-computed on every
 * dye; only the OKLAB path needs the hex.
 */
function deltaEFor(formula, hexA, hexB, labA, labB) {
    switch (formula) {
        case 'ciede2000':
            return ColorConverter.getDeltaE2000(labA, labB);
        case 'oklab':
            return ColorConverter.getDeltaE_Oklab(hexA, hexB);
        case 'cie76':
            return ColorConverter.getDeltaE76(labA, labB);
    }
}
/**
 * Color harmony generator
 * Per R-4: Single Responsibility - harmony generation only
 */
export class HarmonyGenerator {
    database;
    search;
    constructor(database, search) {
        this.database = database;
        this.search = search;
    }
    /**
     * Find dyes that form a complementary color pair
     * Excludes Facewear dyes (generic names like "Red", "Blue")
     * @param hex Base hex color
     * @param options Matching algorithm options
     */
    findComplementaryPair(hex, options) {
        this.database.ensureLoaded();
        try {
            // BUG-047 (2026-07-18 audit): exclude the base dye — mirroring
            // findHarmonyDyesByOffsets. For near-neutral inputs the inverse of a
            // mid-gray is essentially the same color, so without the exclusion the
            // "complement" was the base dye itself.
            const baseDye = this.search.findClosestDye(hex);
            const exclude = baseDye ? [baseDye.id] : [];
            const colorSpace = options?.colorSpace;
            // Non-HSV color spaces: use 180° hue rotation instead of RGB invert
            if (colorSpace && colorSpace !== 'hsv') {
                const complementaryHex = this.rotateHueInSpace(hex, 180, colorSpace);
                return this.findClosestNonFacewearDye(complementaryHex, exclude);
            }
            const complementaryHex = ColorManipulator.invert(hex);
            // Use DeltaE matching if requested
            if (options?.algorithm === 'deltaE') {
                return this.findClosestByDeltaE(complementaryHex, new Set(exclude), options);
            }
            // Find closest dye, excluding Facewear
            return this.findClosestNonFacewearDye(complementaryHex, exclude);
        }
        catch {
            // Invalid hex color
            return null;
        }
    }
    /**
     * Find the closest dye that is not Facewear
     *
     * PERF-003: Simplified to O(log n) by leveraging DyeSearch.findClosestDye
     * which already excludes Facewear dyes internally (CORE-BUG-005).
     * The previous O(n²) loop was redundant since findClosestDye never returns Facewear.
     */
    findClosestNonFacewearDye(hex, excludeIds = []) {
        // DyeSearch.findClosestDye already excludes Facewear dyes (CORE-BUG-005)
        return this.search.findClosestDye(hex, { excludeIds });
    }
    /**
     * Find analogous dyes (adjacent on color wheel)
     * Returns dyes at ±angle degrees from the base color
     *
     * @param hex Base hex color
     * @param angle Hue offset in degrees (default: 30)
     * @param options Matching algorithm options
     *
     * @remarks
     * May return fewer dyes than expected if no suitable matches exist
     * at the target hue positions or if all candidates are excluded.
     */
    findAnalogousDyes(hex, angle = 30, options) {
        // Use harmony helper to find dyes at +angle and -angle positions
        return this.findHarmonyDyesByOffsets(hex, [angle, -angle], {}, options);
    }
    /**
     * Find triadic color scheme (colors 120° apart on color wheel)
     *
     * @param hex Base hex color
     * @param options Matching algorithm options
     *
     * @remarks
     * May return 0, 1, or 2 dyes depending on available matches.
     * Use the length of the returned array to determine actual results.
     */
    findTriadicDyes(hex, options) {
        return this.findHarmonyDyesByOffsets(hex, [120, 240], {}, options);
    }
    /**
     * Find square color scheme (colors 90° apart on color wheel)
     *
     * @param hex Base hex color
     * @param options Matching algorithm options
     *
     * @remarks
     * May return fewer than 3 dyes if suitable matches are not found.
     */
    findSquareDyes(hex, options) {
        return this.findHarmonyDyesByOffsets(hex, [90, 180, 270], {}, options);
    }
    /**
     * Find tetradic color scheme (two complementary pairs)
     *
     * @param hex Base hex color
     * @param options Matching algorithm options
     *
     * @remarks
     * May return fewer than 3 dyes if suitable matches are not found.
     */
    findTetradicDyes(hex, options) {
        // Two adjacent hues + their complements (e.g., base+60 and base+240)
        return this.findHarmonyDyesByOffsets(hex, [60, 180, 240], {}, options);
    }
    /**
     * Find inverted tetradic color scheme (two complementary pairs, mirrored)
     *
     * The mirror rectangle of tetradic: the second pair sits at −60° (300°)
     * instead of +60°, biasing the palette to the opposite side of the wheel.
     *
     * @param hex Base hex color
     * @param options Matching algorithm options
     *
     * @remarks
     * May return fewer than 3 dyes if suitable matches are not found.
     */
    findInvertedTetradicDyes(hex, options) {
        // Two complementary pairs: base↔base+180 and base+120↔base+300 (−60)
        return this.findHarmonyDyesByOffsets(hex, [120, 180, 300], {}, options);
    }
    /**
     * Find monochromatic dyes (same hue, varying saturation/brightness)
     * Excludes Facewear dyes (generic names like "Red", "Blue")
     *
     * @param hex Base hex color
     * @param limit Maximum number of dyes to return (default: 6)
     * @param options Matching algorithm options
     */
    findMonochromaticDyes(hex, limit = 6, options) {
        this.database.ensureLoaded();
        const baseDye = this.findClosestNonFacewearDye(hex);
        if (!baseDye)
            return [];
        const dyes = this.database.getDyesInternal();
        // DeltaE-based monochromatic search
        if (options?.algorithm === 'deltaE') {
            const baseLab = ColorConverter.hexToLab(baseDye.hex);
            const formula = normalizeDeltaEFormula(options.deltaEFormula ?? DEFAULT_HARMONY_DELTA_E);
            const tolerance = options.deltaETolerance ?? DEFAULT_DELTA_E_TOLERANCE[formula];
            const results = [];
            for (const dye of dyes) {
                if (dye.category === 'Facewear' || dye.id === baseDye.id)
                    continue;
                // Use pre-computed LAB (always available for DyeInternal)
                const dyeLab = dye.lab;
                // BUG-059: a two-way ternary silently sent 'oklab' down the CIE76
                // branch, so an oklab caller got CIE76 numbers under an oklab label.
                const deltaE = deltaEFor(formula, baseDye.hex, dye.hex, baseLab, dyeLab);
                // For monochromatic, we want colors that are perceptually similar
                // but have varying lightness (L component)
                if (deltaE <= tolerance) {
                    const satDiff = Math.abs(dye.hsv.s - baseDye.hsv.s);
                    const valDiff = Math.abs(dye.hsv.v - baseDye.hsv.v);
                    results.push({ dye, deltaE, satValDiff: satDiff + valDiff });
                }
            }
            // Sort by saturation/value difference (prefer more variety)
            results.sort((a, b) => b.satValDiff - a.satValDiff);
            return results.slice(0, limit).map((item) => item.dye);
        }
        // Hue-based monochromatic search (original algorithm)
        const baseHue = baseDye.hsv.h;
        const results = [];
        // Find dyes with similar hue but different saturation/value
        for (const dye of dyes) {
            // Skip Facewear dyes (generic names)
            if (dye.category === 'Facewear')
                continue;
            const hueDiff = Math.min(Math.abs(dye.hsv.h - baseHue), 360 - Math.abs(dye.hsv.h - baseHue));
            // Hue must be very close (within ±15°)
            if (hueDiff <= 15 && dye.id !== baseDye.id) {
                // Calculate difference in saturation and value
                const satDiff = Math.abs(dye.hsv.s - baseDye.hsv.s);
                const valDiff = Math.abs(dye.hsv.v - baseDye.hsv.v);
                const satValDiff = satDiff + valDiff;
                results.push({ dye, satValDiff });
            }
        }
        // Sort by saturation/value difference (prefer more variety)
        results.sort((a, b) => b.satValDiff - a.satValDiff);
        return results.slice(0, limit).map((item) => item.dye);
    }
    /**
     * Find split-complementary harmony (±30° from the complementary hue)
     *
     * @param hex Base hex color
     * @param options Matching algorithm options
     *
     * @remarks
     * May return 0, 1, or 2 dyes depending on available matches.
     */
    findSplitComplementaryDyes(hex, options) {
        return this.findHarmonyDyesByOffsets(hex, [150, 210], {}, options);
    }
    /**
     * Generic helper for hue-based and DeltaE-based harmonies
     *
     * @remarks
     * This method may return fewer dyes than the number of offsets provided.
     * This can happen when:
     * - No dyes exist near the target hue position
     * - All candidate dyes are in the 'Facewear' category (excluded)
     * - The same dye would be selected multiple times (prevented by usedDyeIds)
     *
     * Consumers should check the returned array length rather than assuming
     * a fixed number of results.
     *
     * @param hex - Base hex color
     * @param offsets - Array of hue offsets in degrees
     * @param internalOptions - Internal options (tolerance for hue-based matching)
     * @param harmonyOptions - User-provided harmony options
     * @returns Array of matched dyes (may be shorter than offsets array)
     */
    findHarmonyDyesByOffsets(hex, offsets, internalOptions = {}, harmonyOptions) {
        this.database.ensureLoaded();
        const baseDye = this.search.findClosestDye(hex);
        if (!baseDye)
            return [];
        const usedDyeIds = new Set([baseDye.id]);
        const results = [];
        const colorSpace = harmonyOptions?.colorSpace;
        // Non-HSV color space: rotate hue in selected space, match via k-d tree
        if (colorSpace && colorSpace !== 'hsv') {
            for (const offset of offsets) {
                const targetHex = this.rotateHueInSpace(baseDye.hex, offset, colorSpace);
                const match = this.findClosestNonFacewearDye(targetHex, [...usedDyeIds]);
                if (match) {
                    results.push(match);
                    usedDyeIds.add(match.id);
                }
            }
            return results;
        }
        // Check if using DeltaE algorithm
        if (harmonyOptions?.algorithm === 'deltaE') {
            // For DeltaE, we still calculate target colors from hue offsets,
            // but match using perceptual distance instead of hue distance
            const baseHsv = baseDye.hsv;
            for (const offset of offsets) {
                const targetHue = (baseHsv.h + offset + 360) % 360;
                // Generate target color at that hue (keeping same saturation/value)
                const targetHex = ColorConverter.hsvToHex(targetHue, baseHsv.s, baseHsv.v);
                const match = this.findClosestByDeltaE(targetHex, usedDyeIds, harmonyOptions);
                if (match) {
                    results.push(match);
                    usedDyeIds.add(match.id);
                }
            }
            return results;
        }
        // Original hue-based logic (HSV color space)
        const baseHue = baseDye.hsv.h;
        const tolerance = harmonyOptions?.hueTolerance ?? internalOptions.tolerance ?? 45;
        for (const offset of offsets) {
            const targetHue = (baseHue + offset + 360) % 360;
            const match = this.findClosestDyeByHue(targetHue, usedDyeIds, tolerance);
            if (match) {
                results.push(match);
                usedDyeIds.add(match.id);
            }
        }
        return results;
    }
    /**
     * Rotate a color's hue by an offset in the specified color space
     * Keeps the other components (chroma/saturation, lightness/value) unchanged
     *
     * @deprecated Since 5.2.0. Rotates hue at fixed L/C in a space that does not
     * carry the base's S/V; now gamut-maps for `oklch`; still prefer
     * `generateHarmonySlots(…, { wheel })` and `getColorWheel(id)`. Kept for
     * published-API compatibility only.
     *
     * `lch` (and `hsl`) still CLIP per channel, which changes hue on any
     * out-of-gamut rotation — CIELAB has no in-package gamut mapper, and adding
     * one to a deprecated path is not the trade. `oklch` does not: it bisects
     * chroma at constant L and h (CSS Color 4), the same mapping the wheels use.
     */
    rotateHueInSpace(hex, offset, space) {
        switch (space) {
            case 'oklch': {
                const oklch = ColorConverter.hexToOklch(hex);
                return ColorConverter.gamutMapOklch(oklch.L, oklch.C, (oklch.h + offset + 360) % 360);
            }
            case 'lch': {
                const lch = ColorConverter.hexToLch(hex);
                return ColorConverter.lchToHex(lch.L, lch.C, (lch.h + offset + 360) % 360);
            }
            case 'hsl': {
                const hsl = ColorConverter.hexToHsl(hex);
                return ColorConverter.hslToHex((hsl.h + offset + 360) % 360, hsl.s, hsl.l);
            }
            default:
                return hex;
        }
    }
    /**
     * Find closest dye using DeltaE color difference
     * @param targetHex - Target color in hex format
     * @param excludeIds - Dye IDs to exclude from results
     * @param options - DeltaE options (formula, tolerance)
     */
    findClosestByDeltaE(targetHex, excludeIds, options) {
        const formula = normalizeDeltaEFormula(options.deltaEFormula ?? DEFAULT_HARMONY_DELTA_E);
        const tolerance = options.deltaETolerance ?? DEFAULT_DELTA_E_TOLERANCE[formula];
        const targetLab = ColorConverter.hexToLab(targetHex);
        let bestMatch = null;
        let bestDeltaE = Infinity;
        const dyes = this.database.getDyesInternal();
        for (const dye of dyes) {
            if (excludeIds.has(dye.id) || dye.category === 'Facewear') {
                continue;
            }
            // Use pre-computed LAB (always available for DyeInternal)
            const dyeLab = dye.lab;
            // BUG-059: see deltaEFor — 'oklab' used to fall through to CIE76.
            const deltaE = deltaEFor(formula, targetHex, dye.hex, targetLab, dyeLab);
            if (deltaE < bestDeltaE) {
                bestDeltaE = deltaE;
                bestMatch = dye;
            }
        }
        // Only return if within tolerance
        return bestDeltaE <= tolerance ? bestMatch : null;
    }
    /**
     * Find closest dye by hue difference with graceful fallback
     * Per P-2: Uses hue-indexed map for 70-90% speedup
     */
    findClosestDyeByHue(targetHue, usedIds, tolerance) {
        let withinTolerance = null;
        let bestOverall = null;
        // Per P-2: Only search relevant hue buckets instead of all dyes
        const bucketsToSearch = this.database.getHueBucketsToSearch(targetHue, tolerance);
        for (const bucket of bucketsToSearch) {
            const dyesInBucket = this.database.getDyesByHueBucket(bucket);
            for (const dye of dyesInBucket) {
                if (usedIds.has(dye.id) || dye.category === 'Facewear') {
                    continue;
                }
                const diff = Math.min(Math.abs(dye.hsv.h - targetHue), 360 - Math.abs(dye.hsv.h - targetHue));
                if (!bestOverall || diff < bestOverall.diff) {
                    bestOverall = { dye, diff };
                }
                if (diff <= tolerance) {
                    if (!withinTolerance || diff < withinTolerance.diff) {
                        withinTolerance = { dye, diff };
                    }
                }
            }
        }
        return withinTolerance?.dye ?? bestOverall?.dye ?? null;
    }
}
//# sourceMappingURL=HarmonyGenerator.js.map